import {
	HubSpotAuthType,
	HubSpotConnectionDetails,
} from '../../interfaces/hubspot/hubspot-connection-details';
import { DestinationTaskConfigBase } from '../config-objects/destination-service-config-base';
import { Transformation } from '../data-mapper/transformation';

/**
 * Configuration for a HubspotSaveObjects task — creates or updates HubSpot objects
 * using records from the flow payload. Supports single-record mode (per-record create/update)
 * and batch mode (batch create or batch upsert via the HubSpot batch API).
 */
export class HubSpotSaveObjectsConfig
	extends DestinationTaskConfigBase
	implements HubSpotConnectionDetails
{
	/**
	 * HubSpot private app access token. Supports expression syntax.
	 * Leave blank to use the iHub credential store.
	 * @default ''
	 */
	accessToken = '';

	/** Chooses between a static/variable Private App token vs a reusable Web App OAuth connection. @default HubSpotAuthType.PrivateAppToken */
	authType: HubSpotAuthType = HubSpotAuthType.PrivateAppToken;
	/** Selected `OAuthConnectionConfig.id` (provider=HubSpot). WebApp auth only. */
	connectionID?: string;

	/**
	 * HubSpot object type to create/update (e.g. 'contacts', 'companies', 'deals').
	 * Use lowercase HubSpot API names.
	 * @default 'Contacts'
	 */
	hubSpotObjectType = 'Contacts';

	/**
	 * HubSpot property name used as the unique identifier for batch upsert.
	 * The value of this property is read from the mapped data and used to match existing records.
	 * Only used in batch upsert mode (singleExecute=false, batchCreate=false).
	 * @default 'email'
	 */
	idProperty = 'email';

	/**
	 * Transformation rules mapping processingData fields to HubSpot property names.
	 * The service reads sourceMappings[0].mappingFields[0].objectKey as an expression
	 * and writes to destinationMapping.mappingFields[0].objectKey (the HubSpot property API name).
	 * If empty, the entire processingData object is sent as-is.
	 * @default []
	 */
	fieldMappings: Transformation[] = [];

	/**
	 * When true, processes records one at a time using individual HubSpot API calls
	 * (create or update per record based on hubspotObjectId). When false, uses the batch API.
	 * @default undefined (falsy — batch mode)
	 */
	singleExecute?: boolean;

	/**
	 * Only applies when singleExecute is false.
	 * When true, uses the HubSpot batch create API (create-only, fails if object exists).
	 * When false, uses the batch upsert API (matches on idProperty).
	 * @default undefined (falsy — batch upsert)
	 */
	batchCreate?: boolean;

	/**
	 * Expression resolving to the HubSpot object ID of an existing record to update.
	 * When blank or resolves to empty string, a new object is created.
	 * Only used in singleExecute mode.
	 * @default undefined
	 */
	hubspotObjectId?: string;

	/**
	 * When true, treats accessToken as a flow variable expression resolved at runtime.
	 * When false, accessToken is used as a literal string.
	 * @default undefined (falsy)
	 */
	useVariableToken?: boolean;

	constructor(init?: Partial<HubSpotSaveObjectsConfig>) {
		super(init);
		if (init) {
			Object.assign(this, init);
		}
	}
}
